import { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import useIntersection from 'react-use/lib/useIntersection'
import { useAnimation } from 'framer-motion'

import Image from './Image'

const LogosGrid = ({ logos, ...rest }) => {
  const theme = useTheme()

  const ref = useRef()

  const visible = useRef(false)

  const controls = useAnimation()

  const variants = {
    hidden: {
      opacity: 0,
      scale: 0.5,
    },
    visible: (i) => ({
      opacity: 1,
      scale: 1,
      transition: {
        delay: i * theme.motion.timing.short,
      },
    }),
  }

  const intersection = useIntersection(ref, {
    rootMargin: '0px',
    threshold: 0.25,
  })

  useEffect(() => {
    if (intersection?.isIntersecting && !visible.current) {
      visible.current = true

      controls.start('visible')
    }
  }, [intersection, controls])

  return (
    <div
      ref={ref}
      css={{
        background: theme.color.border.base,
        display: 'grid',
        gap: 1,

        [theme.mq.small]: {
          gridTemplateColumns: 'repeat(2, 1fr)',
        },
      }}
      {...rest}
    >
      {logos.map((logo, index) => (
        <div
          css={{
            background: theme.color.light.base,
            padding: theme.space.large,
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
          }}
          key={logo.name}
        >
          <Image
            css={{
              position: 'relative',
              transformOrigin: 'center center',
            }}
            initial="hidden"
            variants={variants}
            animate={controls}
            custom={index + 1}
            style={{
              width: logo.width,
              top: logo.offset ? logo.offset : 0,
            }}
            className="LogosGrid__Logo"
            src={logo.src}
            alt={logo.name}
          />
        </div>
      ))}
      {/* Used to fill-up last item if we have an odd-number to prevent the background border from seeping-through */}
      {logos.length % 2 !== 0 && (
        <div
          css={{
            background: theme.color.light.base,
          }}
        />
      )}
    </div>
  )
}

LogosGrid.propTypes = {
  logos: PropTypes.array.isRequired,
}

export default LogosGrid
